import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import { useMutation, useQuery } from "@apollo/client";

import { 
DeleteMarathonEventMutation,
DeleteMarathonEventMutationVariables,
GetAllMarathonEventsQuery,
GetMarathonEventQuery,
GetMarathonEventQueryVariables,
MarathonMilestoneConfig,
UpdateMarathonEventMutation,
UpdateMarathonEventMutationVariables,
GetAllCategoriesForMarathonQuery
} from "../../../../../../__gqltypes__";
import { eventMatcherDefaultConfig, EventMatchers, EventMatchersConfig, userIdsArrayToString, userIdsStringToArray } from "../../../general/EventMatchers";
import { EventBaseData, EventBaseDataConfig, eventBaseDataConfigDefault } from "../../../general/EventBaseData";
import { DeleteModal } from "../../../general/DeleteModal";
import { DELETE_MARATHON_EVENT, GET_MARATHON_EVENT, UPDATE_MARATHON_EVENT } from "../graphql";
import { MarathonMilestonesData, milestoneConfigToRuntime, MMilestone } from "./MarathonMilestonesData";
import { LoadingLogo } from "../../../../devtools/components/Modal";

type MarathonEventConfig = GetAllMarathonEventsQuery['liveOps']['getAllMarathonEvents'][0];
type MarathonCategoryData = GetAllCategoriesForMarathonQuery['liveOps']['getAllCategoriesForMarathon'];

type Props = {
  eventId: string;
  onUpserEvent: (value: MarathonEventConfig) => void;
  onRemoveEvent: (eventId: string) => void;
  categoryData: MarathonCategoryData,
};

export function MarathonEventData({eventId, onRemoveEvent, onUpserEvent, categoryData}: Props): JSX.Element {

  const {data: eventData, loading: eventDataLoading, refetch} = useQuery<
    GetMarathonEventQuery,
    GetMarathonEventQueryVariables
  >(GET_MARATHON_EVENT, {
      variables: {
        id: eventId,
      },
      fetchPolicy: 'no-cache',
    }
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [baseData, setBaseData] = useState<EventBaseDataConfig>(eventBaseDataConfigDefault);
  const [filterConfig, setFilterConfig] = useState<EventMatchersConfig>(eventMatcherDefaultConfig);
  const [category, setCategory] = useState('rock');
  const [milestones, setMilestones] = useState<MMilestone[]>([]);

  useEffect(() => {
    if (eventData && eventData.liveOps.getMarathonEvent) {
      const mEvent = eventData.liveOps.getMarathonEvent;
      setBaseData({
        name: mEvent.name,
        startDate: mEvent.startDate,
        stopDate: mEvent.stopDate,
        status: mEvent.status,
      });
      setFilterConfig({
        filterAppVersion: mEvent.filterAppVersion ?? undefined,
        filterSubscriptionTier: mEvent.filterSubscriptionTier ?? undefined,
        filterUserIds: userIdsArrayToString(mEvent.filterUserIds ?? []),
        filterUserLevel: mEvent.filterUserLevel ?? undefined,
        filterUserMinCreationDate: mEvent.filterUserMinCreationDate ?? undefined,
        filterUserMaxCreationDate: mEvent.filterUserMaxCreationDate ?? undefined,
      });
      setCategory(mEvent.category ?? 'rock');
      setMilestones(milestoneConfigToRuntime(mEvent.milestones));
    }
  }, [eventData]);

  const categoryOptions = useMemo(() => {
    return categoryData.map((cd) => {
        const v = `${cd.name} [Packs: ${cd.contentPacks}] [Songs: ${cd.songs}]`;
        return (
          <option key={cd.id} value={cd.id}>
            {v}
          </option>
        );
      });
  }, [categoryData]);
  
  const handleChangeCategory = useCallback((event) => {
    setCategory(event.currentTarget.value);
  }, []);

  const [updateEvent, {loading: updateLoading}] = useMutation<
    UpdateMarathonEventMutation,
    UpdateMarathonEventMutationVariables
  >(UPDATE_MARATHON_EVENT,
    {
      onCompleted: ({updateMarathonEvent}) => {
        const updated = updateMarathonEvent.event;
        onUpserEvent(updated);
        refetch();
      },
      onError: (error) => {
        console.log(error);
      },
    }
  );

  const [deleteEvent, { loading: deleteLoading }] = useMutation<
    DeleteMarathonEventMutation,
    DeleteMarathonEventMutationVariables
   >(DELETE_MARATHON_EVENT,
      {
        onCompleted: ({deleteMarathonEvent}) => {
          if (deleteMarathonEvent) {
            onRemoveEvent(eventId);
          } else {
            alert("Error: couldn't remove the current Wheel Fortune");
          }
        },
        onError: (error) => {
          console.log(error);
        },
      }
   );

   const handleDelete = useCallback(() => {
    setShowDeleteModal(false);
    deleteEvent({
      variables: {
        id: eventId,
      }
    });
  }, [deleteEvent, eventId]);

  const handleSave = () => {
    updateEvent({
      variables: {
        input: {
          id: eventId,
          ...baseData,
          category,
          milestones,
          ...filterConfig,
          filterUserIds: userIdsStringToArray(filterConfig.filterUserIds ?? ''),
        }
      }
    });
  };

  if (eventDataLoading || updateLoading || deleteLoading) {
    return (<LoadingLogo show />);
  }

  return (<>
    <Container className="pb-4">
      <EventBaseData 
        eventId={eventId}
        data={baseData}
        onUpdate={setBaseData}
        onShowDeleteModal={() => setShowDeleteModal(true)}
        onSave={handleSave}
        />
      <EventMatchers config={filterConfig} onUpdate={setFilterConfig}/>
      <Row className="mb-3">
        <Col md={3}>Category:</Col>
        <Col md={4}>
          <Form.Group controlId="sale-event-status">
            <Form.Control as="select" type="text" value={category} onChange={handleChangeCategory}>
              {categoryOptions}
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>
      <MarathonMilestonesData milestones={milestones} onUpdateEvent={setMilestones}/>
      <DeleteModal
        title="Marathon Event"
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        onDelete={handleDelete}/>
    </Container>
  </>);
}